/**
 * 69.50 step 1 (DIRECTION §0.13; DECISIONS 257, 258): the story's objective kinds as one list on the action clock
 * (`modeState.objectives`, with GameConfig.storyObjectives), read by the bots (ai/squadOrders.ts), the HUD and the win
 * (`objectives`, core/winConditions.ts). Three kinds first, §0.13's table:
 *   - **take:** the side owns its point (capture's rules, as built).
 *   - **hold for a time:** the side holds its point uncontested for `holdSeconds` — the clock runs while it owns it and
 *     nobody is taking it, stands while it is being taken, and starts again from nothing when it is lost.
 *   - **destroy a set:** every target gone — a structure (gone, or at 0 health) or one of its marked parts (at 0:
 *     a point-defence mount's radar head or gun, with partial destruction on).
 * Done is latched (`doneTick`), and an `objectiveDone` event is told once.
 *
 * Toward 257 (4) — stories from objectives and the planet's setup alone — the default list is derived from the setup,
 * not written (`deriveObjectives`): each side is to hold the objective between the bases (the one most nearly equally far
 * from both) for STORY.holdSeconds, to take the high ground in the other's half (69.61, from the terrain), and to destroy the other side's air defence: its forward air-defence site (69.50 step
 * 2a, GameConfig.airDefenceSites — §0.13's Act II), else with point defence on its bases' mounts. 69.61 step 4 (GameConfig.storyCarry):
 * to transmit from a point in the other's half (an upload held for STORY.uploadSeconds, kept when driven off) and to carry an
 * item from another there to its base (taken up within reach, dropped where its carrier falls, returned by the other side's
 * touch). 69.61 step 5 (GameConfig.storyEscort, with mountedAssault): to escort a column of its vehicles to a point there — its
 * mounted play at the point marching (the convoy, core/convoyMarch.ts), done when the convoy halts at its jump-off in its
 * wedge; halted short by contact (the herringbone), or the play over before its halt, the column is stopped. Pure.
 */
import type { GameState, StoryObjective, Vec3 } from '../types';
import { STORY } from '../constants';
import { partHealth } from './parts';
import { arcDistance } from './gameState';
import { isSite } from './pointDefence';
import { getSurfaceRadius } from './surface';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { dcos, dsin } from './dmath';
import { vecAdd, vecNormalize, vecScale, surfaceTangentFrame } from '../math';

/** An objective done this tick; 69.61 step 4, a carry's item taken up, dropped (its carrier down) or returned (by the other side). */
export type ObjectiveEvent =
  | { kind: 'objectiveDone'; objectiveId: number; team: number }
  | { kind: 'itemCarried'; objectiveId: number; team: number; how: 'taken' | 'dropped' | 'returned' }
  | { kind: 'columnMarch'; objectiveId: number; team: number; how: 'marching' | 'stopped'; why?: 'contact' | 'over' };   // (69.61 step 5: an escort's column set off, or stopped short; 69.80: stopped by contact on the march — its herringbone, 311's re-form marching it on — or its play over)

/** Is target `t` gone: its structure absent or at 0, or (a part named) that part at 0. */
export function targetGone(state: Pick<GameState, 'structures'>, t: { structureId: number; part?: number }): boolean {
  const s = state.structures.find(x => x.id === t.structureId);
  if (!s || s.health <= 0) return true;
  return t.part !== undefined && partHealth(s, t.part) <= 0;
}

/** The targets of a destroy objective still standing. */
/**
 * DECISIONS 298 (c), as the coordinator read it after lane C's story-1 run: is `structureId` a target of a destroy objective not
 * yet done (an air-defence site or its generator) — what a striker out of HE restocks for and goes back to. Not the HQ: 284 (a)'s
 * bar is that a lone squad's grenades leave it standing, and restocking, two of an escort's riders felled the 600 HQ in a minute.
 */
export function isDestroyTarget(state: Pick<GameState, 'modeState'>, structureId: number): boolean {
  return (state.modeState.objectives ?? []).some(o => o.kind === 'destroy' && o.doneTick === undefined && (o.targets ?? []).some(t => t.structureId === structureId));
}

export function targetsLeft(state: Pick<GameState, 'structures'>, o: Pick<StoryObjective, 'targets'>): Array<{ structureId: number; part?: number }> {
  return (o.targets ?? []).filter(t => !targetGone(state, t));
}

/**
 * One step of `dt` seconds at `tick`: each objective not yet done moves (a hold's clock) and is latched done when met.
 * Returns the state unchanged (the same object) when nothing moved.
 */
export function stepObjectives(state: GameState, dt: number, tick: number): { state: GameState; events: ObjectiveEvent[] } {
  const list = state.modeState.objectives;
  if (!list?.length) return { state, events: [] };
  const sites = state.modeState.points?.sites ?? [];
  const events: ObjectiveEvent[] = [];
  let out: StoryObjective[] | null = null;
  list.forEach((o, i) => {
    if (o.doneTick !== undefined) return;
    let next = o, done = false;
    if (o.kind === 'take') {
      const p = sites.find(s => s.id === o.pointId);
      done = !!p && p.owner === o.team && p.capturer === null;
    } else if (o.kind === 'hold') {
      const p = sites.find(s => s.id === o.pointId);
      const held = o.held ?? 0;
      const now = !p ? held : p.owner !== o.team ? 0 : p.capturer !== null ? held : held + dt;   // (lost: from nothing; being taken: it stands)
      if (now !== held) next = { ...o, held: now };
      done = now >= (o.holdSeconds ?? STORY.holdSeconds);
    } else if (o.kind === 'transmit') {
      // 69.61 step 4: the upload runs while one of the side's people stands on the point and none of the other's; kept when driven off
      const p = sites.find(s => s.id === o.pointId);
      const on = p ? peopleWithin(state, p.planetIndex, p.surfaceDir, state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius) : [];
      const held = o.held ?? 0;
      const now = on.some(u => u.teamIndex === o.team) && !on.some(u => u.teamIndex !== o.team) ? held + dt : held;
      if (now !== held) next = { ...o, held: now };
      done = now >= (o.holdSeconds ?? STORY.uploadSeconds);
    } else if (o.kind === 'carry') {
      const r = stepCarry(state, o, sites);
      next = r.next;
      done = r.delivered;
      if (r.how) events.push({ kind: 'itemCarried', objectiveId: o.id, team: o.team, how: r.how });
    } else if (o.kind === 'escort') {
      const r = stepEscort(state, o);
      next = r.next;
      done = r.arrived;
      if (r.how) events.push({ kind: 'columnMarch', objectiveId: o.id, team: o.team, how: r.how, ...(r.why ? { why: r.why } : {}) });
    } else {
      done = (o.targets?.length ?? 0) > 0 && targetsLeft(state, o).length === 0;
    }
    if (done) { next = { ...next, doneTick: tick }; events.push({ kind: 'objectiveDone', objectiveId: o.id, team: o.team }); }
    if (next !== o) (out ??= list.slice())[i] = next;
  });
  return out ? { state: { ...state, modeState: { ...state.modeState, objectives: out } }, events } : { state, events };
}

/** The living, grounded people (not landing, not aboard) within `reach` (world units) of a place — capture's own rule (core/capturePoints.ts standingOn). */
function peopleWithin(state: Pick<GameState, 'players' | 'planets'>, planetIndex: number, dir: Vec3, reach: number): GameState['players'] {
  const R = state.planets[planetIndex]?.radius ?? 1;
  return state.players.filter(p => p.alive && !p.airborne && !((p.landingLeft ?? 0) > 0) && !p.boarded && p.planetIndex === planetIndex && arcDistance(p.surfaceDir, dir, R) <= reach);
}

/**
 * 69.61 step 4: one tick of a carry objective's item. Carried, it is where its carrier is, and delivered within
 * STORY.deliverPeople of the side's base; its carrier down (dead, or gone), it drops where it was. At rest, a person of the
 * other side within STORY.carryReachPeople returns it to its point (when it lies away from it), else one of the side's
 * (the lowest id) takes it up. Pure.
 */
function stepCarry(state: GameState, o: StoryObjective, sites: Array<{ id: number; planetIndex: number; surfaceDir: Vec3 }>): { next: StoryObjective; delivered: boolean; how?: 'taken' | 'dropped' | 'returned' } {
  const item = o.item;
  if (!item) return { next: o, delivered: false };
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = state.planets[item.planetIndex]?.radius ?? 1;
  if (item.carrierId !== undefined) {
    const c = state.players.find(p => p.id === item.carrierId);
    if (!c || !c.alive) return { next: { ...o, item: { planetIndex: item.planetIndex, surfaceDir: item.surfaceDir } }, delivered: false, how: 'dropped' };
    const base = state.board?.bases.find(b => b.team === o.team);
    const delivered = !!base && base.planetIndex === c.planetIndex && arcDistance(c.surfaceDir, base.dir, state.planets[c.planetIndex].radius) <= STORY.deliverPeople * person;
    const moved = c.planetIndex !== item.planetIndex || c.surfaceDir !== item.surfaceDir;
    if (delivered) return { next: { ...o, item: { planetIndex: c.planetIndex, surfaceDir: c.surfaceDir } }, delivered: true };
    return { next: moved ? { ...o, item: { planetIndex: c.planetIndex, surfaceDir: c.surfaceDir, carrierId: c.id } } : o, delivered: false };
  }
  const near = peopleWithin(state, item.planetIndex, item.surfaceDir, STORY.carryReachPeople * person);
  if (!near.length) return { next: o, delivered: false };
  const home = sites.find(s => s.id === o.pointId);
  const away = !!home && (home.planetIndex !== item.planetIndex || arcDistance(item.surfaceDir, home.surfaceDir, R) > STORY.carryReachPeople * person);
  if (away && home && near.some(u => u.teamIndex !== o.team)) return { next: { ...o, item: { planetIndex: home.planetIndex, surfaceDir: home.surfaceDir } }, delivered: false, how: 'returned' };
  const taker = near.filter(u => u.teamIndex === o.team).sort((a, b) => a.id - b.id)[0];
  return taker ? { next: { ...o, item: { planetIndex: taker.planetIndex, surfaceDir: taker.surfaceDir, carrierId: taker.id } }, delivered: false, how: 'taken' } : { next: o, delivered: false };
}

/**
 * 69.61 step 5: one tick of an escort objective. Its column is the side's mounted play at its point whose convoy marches
 * (taken up when one sets off: 'marching'). Arrived (done): the convoy halted in its wedge, at its jump-off, and one of its
 * riding squads (a squad with an APC in the play's rides) has a person within STORY.escortPeople of its jump-off — the
 * troops brought, not the vehicles alone. Stopped: halted in the herringbone (contact on the march), or the play over (or
 * gone) before it arrived; the next play's column is watched when it sets off (`why` says which: 'contact', the herringbone, or
 * 'over'). Pure.
 */
function stepEscort(state: GameState, o: StoryObjective): { next: StoryObjective; arrived: boolean; how?: 'marching' | 'stopped'; why?: 'contact' | 'over' } {
  const plays = state.plays ?? [];
  if (o.column === undefined) {
    const p = plays.find(q => q.team === o.team && 'pointId' in q.objective && q.objective.pointId === o.pointId && q.phase !== 'done' && !!q.mounted?.convoy?.order.length && !q.mounted.convoy.halted);
    return p ? { next: { ...o, column: p.id }, arrived: false, how: 'marching' } : { next: o, arrived: false };
  }
  const p = plays.find(q => q.id === o.column), halted = p?.mounted?.convoy?.halted;
  const { column: _, ...rest } = o;
  if (p && halted?.how === 'wedge' && troopsIn(state, p)) return { next: rest, arrived: true };
  if (!p || halted?.how === 'herringbone' || p.phase === 'done') return { next: rest, arrived: false, how: 'stopped', why: p && p.phase !== 'done' ? 'contact' : 'over' };
  return { next: o, arrived: false };
}

/** 69.61 step 5: a mounted play's riding squads — one with a living person within STORY.escortPeople of its jump-off. */
function troopsIn(state: GameState, p: NonNullable<GameState['plays']>[number]): boolean {
  const R = state.planets[p.position.planetIndex]?.radius ?? 1, reach = STORY.escortPeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
  return Object.keys(p.mounted?.rides ?? {}).map(Number).some(sid => {
    const at = p.jumpOff[sid], sq = state.squads?.find(s => s.id === sid);
    return !!at && !!sq && sq.unitIds.some(id => { const u = state.players.find(x => x.id === id); return !!u && u.alive && u.planetIndex === p.position.planetIndex && arcDistance(u.surfaceDir, at, R) <= reach; });
  });
}

/**
 * DECISIONS 298 (e): `team`'s escort riders — once its escort is done (its column halted in the wedge at the point, troops in),
 * the squads that rode the column's APCs (the Match's squad ids, the latest such play's). They are forward and otherwise idle;
 * the commander sends them on the destroy. None while the escort is not done.
 */
export function escortRiders(state: Pick<GameState, 'modeState' | 'plays'>, team: number): number[] {
  const o = (state.modeState.objectives ?? []).find(x => x.team === team && x.kind === 'escort' && x.doneTick !== undefined && x.pointId !== undefined);
  if (!o) return [];
  const p = (state.plays ?? []).filter(q => q.team === team && 'pointId' in q.objective && q.objective.pointId === o.pointId && q.mounted?.convoy?.halted?.how === 'wedge')
    .reduce<NonNullable<GameState['plays']>[number] | undefined>((a, q) => (!a || q.id > a.id ? q : a), undefined);
  return p ? Object.keys(p.mounted!.rides).map(Number).sort((a, b) => a - b) : [];
}

/** 69.61: `team`'s take and hold objectives not yet done, on a point (id order): what its commander sends squads to first; 69.61 step 4, its transmits too. */
export function storyPoints(state: Pick<GameState, 'modeState'>, team: number): Array<StoryObjective & { pointId: number }> {
  return (state.modeState.objectives ?? []).filter((o): o is StoryObjective & { pointId: number } => o.team === team && o.doneTick === undefined && (o.kind === 'take' || o.kind === 'hold' || o.kind === 'transmit') && o.pointId !== undefined);
}

/** 69.96 (DECISIONS 316): how many of `team`'s objectives are done — the Story's verdict at time ranks by it. */
export function objectivesDoneCount(state: Pick<GameState, 'modeState'>, team: number): number {
  return (state.modeState.objectives ?? []).filter(o => o.team === team && o.doneTick !== undefined).length;
}

/** Has `team` done every one of its objectives (and has it any)? The win kind `objectives` reads it. */
export function objectivesDone(state: Pick<GameState, 'modeState'>, team: number): boolean {
  const mine = (state.modeState.objectives ?? []).filter(o => o.team === team);
  return mine.length > 0 && mine.every(o => o.doneTick !== undefined);
}

/**
 * 69.61: how far a point stands above the ground round it (people): its surface radius less the mean of STORY.riseBearings
 * points on a ring STORY.risePeople out — the terrain's read of "the high ground".
 */
export function riseAt(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number, dir: Vec3): number {
  const planet = state.planets[planetIndex];
  if (!planet) return 0;
  const n = vecNormalize(dir), { tx, ty } = surfaceTangentFrame(n), person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const a = (STORY.risePeople * person) / planet.radius;
  let ring = 0;
  for (let k = 0; k < STORY.riseBearings; k++) {
    const t = (2 * Math.PI * k) / STORY.riseBearings;
    const along = vecAdd(vecScale(tx, dcos(t)), vecScale(ty, dsin(t)));
    ring += getSurfaceRadius(planet, vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(along, dsin(a)))));
  }
  return (getSurfaceRadius(planet, n) - ring / STORY.riseBearings) / person;
}

/**
 * The default list, derived from the setup (with GameConfig.storyObjectives on the board): for each side, *hold* the
 * objective between the bases for STORY.holdSeconds; 69.61: *take* the high ground in the other's half — of the objectives
 * nearer the other side's base (the middle left out), its member of the mirrored pair standing highest over the ground
 * round it (`riseAt`; a pair's worth its lower rise), so each side's walk to its own is the other's; and *destroy* the other side's air defence
 * (its forward site and generator, else its mounts, when standing). Empty without a board or capture points.
 */
export function deriveObjectives(state: GameState): StoryObjective[] {
  const bases = state.board?.bases ?? [], sites = state.modeState.points?.sites ?? [];
  if (bases.length < 2 || !sites.length) return [];
  const planetIndex = bases[0].planetIndex, R = state.planets[planetIndex]?.radius ?? 1;
  const on = sites.filter(s => s.planetIndex === planetIndex);
  const out: StoryObjective[] = [];
  // the objective between the bases: the most nearly equally far from both (the first on a tie)
  const [a, b] = bases;
  const middle = on.length ? on.reduce((best, s) => (Math.abs(arcDistance(s.surfaceDir, a.dir, R) - arcDistance(s.surfaceDir, b.dir, R)) < Math.abs(arcDistance(best.surfaceDir, a.dir, R) - arcDistance(best.surfaceDir, b.dir, R)) ? s : best)) : null;
  const rise = new Map(on.map(s => [s.id, riseAt(state, planetIndex, s.surfaceDir)]));
  // (69.61 step 2: the high ground as a mirrored pair — each objective in team b's half with its mirror in a's, the one as far
  // from a's base as it is from b's; the pair's worth the lower rise of the two; each side takes the member in the other's
  // half, so the two walks are the same: picked side by side, one side's stood 3,043 people out by the other's base and the
  // other's just past the middle)
  const near = (s: typeof on[number], x: typeof a) => arcDistance(s.surfaceDir, x.dir, R);
  const inHalf = (x: typeof a, y: typeof a) => on.filter(s => s !== middle && near(s, y) < near(s, x));   // nearer y's base
  let pair: { inA: typeof on[number]; inB: typeof on[number]; worth: number } | null = null;
  for (const s of inHalf(a, b)) {
    const m = inHalf(b, a).reduce<typeof on[number] | null>((best, x) => (!best || Math.abs(near(x, a) - near(s, b)) + Math.abs(near(x, b) - near(s, a)) < Math.abs(near(best, a) - near(s, b)) + Math.abs(near(best, b) - near(s, a)) ? x : best), null);
    if (!m) continue;
    const worth = Math.min(rise.get(s.id)!, rise.get(m.id)!);
    if (!pair || worth > pair.worth) pair = { inA: m, inB: s, worth };
  }
  // (69.61 step 4, GameConfig.storyCarry: two more mirrored pairs from the points left — the best mirrors first, each point once;
  // the more central the transmit's, the deeper the carry's. Step 5, GameConfig.storyEscort with mountedAssault: one more, the
  // deepest of those picked the escort's — the column's march the longest)
  let more: { transmit: typeof pair; carry: typeof pair } | null = null, escort: typeof pair = null;
  const carry = !!state.config.storyCarry, column = !!state.config.storyEscort && !!state.config.mountedAssault;
  const want = (carry ? 2 : 0) + (column ? 1 : 0);
  if (want) {
    const used = new Set([middle?.id, pair?.inA.id, pair?.inB.id]);
    const err = (s: typeof on[number], m: typeof on[number]) => Math.abs(near(m, a) - near(s, b)) + Math.abs(near(m, b) - near(s, a));
    const cands = inHalf(a, b).filter(s => !used.has(s.id)).flatMap(s => inHalf(b, a).filter(m => !used.has(m.id)).map(m => ({ inA: m, inB: s, worth: 0, e: err(s, m) })))
      .sort((x, y) => x.e - y.e || x.inB.id - y.inB.id || x.inA.id - y.inA.id);
    const picked: Array<NonNullable<typeof pair>> = [];
    for (const c of cands) { if (picked.length >= want) break; if (used.has(c.inA.id) || used.has(c.inB.id)) continue; picked.push({ inA: c.inA, inB: c.inB, worth: 0 }); used.add(c.inA.id); used.add(c.inB.id); }
    if (picked.length === want) {
      const order = [...picked].sort((p, q) => near(q.inB, b) - near(p.inB, b));   // (the farther from b's base its member in b's half, the more central)
      if (column) escort = order.pop()!;
      if (carry) more = { transmit: order[0], carry: order[1] };
    }
  }
  for (const base of bases) {
    const team = base.team, other = bases.find(x => x.team !== team)!;
    if (middle) out.push({ id: out.length, kind: 'hold', team, pointId: middle.id, holdSeconds: STORY.holdSeconds });
    // (69.61: the high ground in their half — the pair's member there)
    const high = pair ? (other === b ? pair.inB : pair.inA) : null;
    if (high) out.push({ id: out.length, kind: 'take', team, pointId: high.id, label: 'the high ground' });
    // (69.61 step 4: transmit from the central pair's member in their half; carry the deeper pair's item home)
    if (more) {
      const up = other === b ? more.transmit!.inB : more.transmit!.inA, from = other === b ? more.carry!.inB : more.carry!.inA;
      out.push({ id: out.length, kind: 'transmit', team, pointId: up.id, holdSeconds: STORY.uploadSeconds, label: 'the uplink' });
      out.push({ id: out.length, kind: 'carry', team, pointId: from.id, item: { planetIndex: from.planetIndex, surfaceDir: from.surfaceDir }, label: 'the intel' });
    }
    // (69.61 step 5: march a column to the escort pair's member in their half)
    if (escort) out.push({ id: out.length, kind: 'escort', team, pointId: (other === b ? escort.inB : escort.inA).id, label: 'the column' });
    const theirs = state.structures.filter(s => s.type === 'pointDefence' && s.health > 0 && state.players.find(p => p.id === s.ownerId)?.teamIndex !== team && state.players.find(p => p.id === s.ownerId)?.teamIndex !== undefined);
    // (69.50 step 2a: the other side's forward air-defence site when it has one — Act II's objective — else its bases' mounts)
    const sites = theirs.filter(isSite), set = sites.length ? sites : theirs;
    // (69.50 step 2b: a site and its generator, both — either down silences it, the set wants both)
    const targets = set.flatMap(s => [{ structureId: s.id }, ...(s.pd?.power !== undefined && state.structures.some(g => g.id === s.pd!.power && g.health > 0) ? [{ structureId: s.pd.power }] : [])]);
    if (set.length) out.push({ id: out.length, kind: 'destroy', team, targets, label: sites.length ? 'air-defence site' : 'air defence' });
  }
  return out;
}

/** The setup's step: the derived list laid on the mode state (nothing when it would be empty: the block stays absent). */
export function withStoryObjectives(state: GameState): GameState {
  const list = deriveObjectives(state);
  return list.length ? { ...state, modeState: { ...state.modeState, objectives: list } } : state;
}
